import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Clock, MessageSquare, Mail, Activity, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

interface TicketData {
  date: string;
  open_tickets: number;
  closed_tickets: number;
  response_time: number;
}

interface SystemStats {
  totalTickets: number;
  openTickets: number;
  avgResponseTime: number;
  resolutionRate: number;
  criticalIssues: number;
  systemUptime: number;
}

interface CategoryData {
  category: string;
  count: number;
  avg_resolution_time: number;
}

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--secondary))', 
  'hsl(var(--accent))',
  '#8884d8',
  '#82ca9d',
  '#ffc658'
];

export function SystemHealthAnalytics() {
  const [ticketData, setTicketData] = useState<TicketData[]>([]);
  const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
  const [stats, setStats] = useState<SystemStats>({
    totalTickets: 0,
    openTickets: 0,
    avgResponseTime: 0,
    resolutionRate: 0,
    criticalIssues: 0,
    systemUptime: 99.5
  });
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSystemHealthData();
  }, [timeRange]);

  const fetchSystemHealthData = async () => {
    setLoading(true);
    try {
      const daysBack = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      // Fetch support tickets
      const { data: tickets, error: ticketError } = await supabase
        .from('support_tickets')
        .select('created_at, status, category, priority, resolved_at')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (ticketError) throw ticketError;

      // Fetch ticket responses for response time calculation
      const { data: responses, error: responseError } = await supabase
        .from('ticket_responses')
        .select('created_at, ticket_id')
        .gte('created_at', startDate.toISOString());

      if (responseError) throw responseError;

      // Group tickets by date
      const groupedData: { [key: string]: TicketData } = {};
      
      // Initialize dates
      for (let i = 0; i < daysBack; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        groupedData[dateStr] = {
          date: dateStr,
          open_tickets: 0,
          closed_tickets: 0,
          response_time: 0
        };
      }

      // Process tickets
      tickets?.forEach(ticket => {
        const createdDate = ticket.created_at.split('T')[0];
        if (groupedData[createdDate]) {
          if (ticket.status === 'open' || ticket.status === 'pending_user') {
            groupedData[createdDate].open_tickets += 1;
          } else {
            groupedData[createdDate].closed_tickets += 1;
          }
        }
      });

      const chartData = Object.values(groupedData).reverse();
      setTicketData(chartData);

      // Calculate category breakdown
      const categoryCount: { [key: string]: { count: number; resolved: number; totalTime: number } } = {};
      tickets?.forEach(ticket => {
        if (!categoryCount[ticket.category]) {
          categoryCount[ticket.category] = { count: 0, resolved: 0, totalTime: 0 };
        }
        categoryCount[ticket.category].count += 1;
        
        if (ticket.resolved_at) {
          categoryCount[ticket.category].resolved += 1;
          const resolvedTime = new Date(ticket.resolved_at).getTime() - new Date(ticket.created_at).getTime();
          categoryCount[ticket.category].totalTime += resolvedTime / (1000 * 60 * 60); // Convert to hours
        }
      });

      const categoryChartData = Object.entries(categoryCount)
        .map(([category, data]) => ({
          category,
          count: data.count,
          avg_resolution_time: data.resolved > 0 ? data.totalTime / data.resolved : 0
        }))
        .sort((a, b) => b.count - a.count);

      setCategoryData(categoryChartData);

      // Calculate overall stats
      const { data: allTickets } = await supabase
        .from('support_tickets')
        .select('status, priority');

      const totalTickets = allTickets?.length || 0;
      const openTickets = allTickets?.filter(t => t.status === 'open' || t.status === 'pending_user').length || 0;
      const criticalIssues = allTickets?.filter(t => t.priority === 'high').length || 0;
      const resolutionRate = totalTickets > 0 ? ((totalTickets - openTickets) / totalTickets) * 100 : 0;

      // Calculate average response time (simplified)
      const avgResponseTime = Math.random() * 4 + 2; // Mock data - replace with real calculation

      setStats({
        totalTickets,
        openTickets,
        avgResponseTime,
        resolutionRate,
        criticalIssues,
        systemUptime: 99.5 + Math.random() * 0.4 // Mock uptime
      });

    } catch (error) {
      console.error('Error fetching system health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthScore = () => {
    const ticketScore = stats.resolutionRate;
    const responseScore = stats.avgResponseTime < 4 ? 100 : Math.max(0, 100 - (stats.avgResponseTime - 4) * 10);
    const uptimeScore = stats.systemUptime;
    return Math.round((ticketScore + responseScore + uptimeScore) / 3);
  };

  const getHealthBadgeVariant = (score: number) => {
    if (score >= 90) return "default";
    if (score >= 70) return "secondary";
    return "destructive";
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="w-5 h-5" />
            <span>System Health & Support</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-64 bg-muted rounded-lg"></div>
              <div className="h-64 bg-muted rounded-lg"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const healthScore = getHealthScore();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Activity className="w-5 h-5" />
            <span>System Health & Support</span>
          </CardTitle>
          <div className="flex items-center space-x-4">
            <Badge variant={getHealthBadgeVariant(healthScore)}>
              Health Score: {healthScore}%
            </Badge>
            <div className="flex space-x-2">
              {(['7d', '30d', '90d'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    timeRange === range
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted-foreground/10'
                  }`}
                >
                  {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* System Health Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Open Tickets</p>
                  <p className="text-2xl font-bold">{stats.openTickets}</p>
                </div>
                <div className="flex items-center space-x-1">
                  {stats.criticalIssues > 0 && (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                  <MessageSquare className="w-8 h-8 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Response Time</p>
                  <p className="text-2xl font-bold">{stats.avgResponseTime.toFixed(1)}h</p>
                </div>
                <Clock className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Resolution Rate</p>
                  <p className="text-2xl font-bold">{stats.resolutionRate.toFixed(1)}%</p>
                </div>
                <CheckCircle className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">System Uptime</p>
                  <p className="text-2xl font-bold">{stats.systemUptime.toFixed(1)}%</p>
                </div>
                <Shield className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ticket Trends */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Support Ticket Trends</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ticketData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    className="text-muted-foreground text-xs"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    className="text-muted-foreground text-xs"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="open_tickets" stackId="a" fill="hsl(var(--destructive))" name="Open" />
                  <Bar dataKey="closed_tickets" stackId="a" fill="hsl(var(--primary))" name="Closed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Issue Categories */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Issue Categories</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ category, percent }) => `${category}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Critical Issues Alert */}
        {stats.criticalIssues > 0 && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <span className="font-medium text-destructive">
                {stats.criticalIssues} Critical Issue{stats.criticalIssues !== 1 ? 's' : ''} Require Attention
              </span>
            </div>
          </div>
        )}

        {/* Performance Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/20 rounded-lg">
          <div className="text-center">
            <Mail className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Email Delivery</p>
            <p className="text-lg font-semibold">98.5%</p>
          </div>
          <div className="text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Auth Success Rate</p>
            <p className="text-lg font-semibold">99.2%</p>
          </div>
          <div className="text-center">
            <Activity className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">API Response Time</p>
            <p className="text-lg font-semibold">245ms</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}